import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
	GatewayError,
	getRovoExtensionAdminSettings,
	type RovoExtAdminSettings,
} from '../gateway-client/gatewayClient';
import { postMessageToParent } from '../../message-bus/postMessageToParent';

export enum SettingsQueryKeys {
	REMOTE_ADMIN_SETTINGS = 'remoteAdminSettings',
}

const CACHE_RESPONSE_TIMEOUT_MS = 2000;
const CACHE_CLEAR_SETTLE_MS = 100;
const ONE_MINUTE_MS = 60 * 1000;

/**
 * Query key for the consolidated `rovoextadminsettings` bucket.
 *
 * Every consumer of admin settings — the settings provider, company theming and
 * company announcements — observes this one key and projects the shape it needs
 * with `select`. React Query therefore performs a single fetch per site,
 * regardless of how many consumers are mounted.
 */
export const getAdminSettingsQueryKey = (
	siteId: string | undefined,
): [string, string | undefined] => [SettingsQueryKeys.REMOTE_ADMIN_SETTINGS, siteId];

function getCachedRemoteAdminSettings(siteId: string): Promise<RovoExtAdminSettings | null> {
	return new Promise((resolve) => {
		const timeoutId = setTimeout(() => {
			window.removeEventListener('message', messageHandler);
			resolve(null);
		}, CACHE_RESPONSE_TIMEOUT_MS);

		const messageHandler = (event: MessageEvent): void => {
			if (event.data.type === 'GET_REMOTE_ADMIN_SETTINGS_CACHE_RESPONSE') {
				clearTimeout(timeoutId);
				window.removeEventListener('message', messageHandler);
				resolve(event.data.data);
			}
		};

		window.addEventListener('message', messageHandler, false);
		postMessageToParent({ type: 'GET_REMOTE_ADMIN_SETTINGS_CACHE', siteId });
	});
}

export function setCachedRemoteAdminSettings(siteId: string, settings: RovoExtAdminSettings): void {
	postMessageToParent({
		type: 'SET_REMOTE_ADMIN_SETTINGS_CACHE',
		siteId,
		settings,
	});
}

/**
 * Clears the chrome.storage.local cache to force a fresh API fetch. Resolves
 * after a short delay so the parent frame has processed the message.
 */
export function clearCachedRemoteAdminSettings(siteId: string): Promise<void> {
	postMessageToParent({
		type: 'CLEAR_REMOTE_ADMIN_SETTINGS_CACHE',
		siteId,
	});
	return new Promise((resolve) => setTimeout(resolve, CACHE_CLEAR_SETTLE_MS));
}

export async function fetchRemoteAdminSettingsWithCache(
	siteId: string,
): Promise<RovoExtAdminSettings> {
	const cachedData = await getCachedRemoteAdminSettings(siteId);

	if (cachedData !== null) {
		return cachedData;
	}

	const freshData = await getRovoExtensionAdminSettings(siteId);
	setCachedRemoteAdminSettings(siteId, freshData);

	return freshData;
}

/**
 * Canonical options for the shared admin-settings query.
 *
 * All consumers must spread these so the cache entry has one consistent
 * lifecycle; the only permitted per-consumer difference is `select` (and
 * `throwOnError`, which React Query evaluates per observer).
 */
export const getAdminSettingsQueryOptions = (
	siteId: string | undefined,
): Pick<
	UseQueryOptions<RovoExtAdminSettings, GatewayError>,
	| 'queryKey'
	| 'queryFn'
	| 'enabled'
	| 'staleTime'
	| 'gcTime'
	| 'refetchOnWindowFocus'
	| 'refetchOnMount'
	| 'retry'
> => ({
	queryKey: getAdminSettingsQueryKey(siteId),
	queryFn: (): Promise<RovoExtAdminSettings> => {
		if (!siteId) {
			throw new Error('siteId is required to fetch remote admin settings');
		}
		return fetchRemoteAdminSettingsWithCache(siteId);
	},
	enabled: !!siteId,
	staleTime: ONE_MINUTE_MS,
	gcTime: Infinity,
	refetchOnWindowFocus: false,
	refetchOnMount: 'always',
	/*
	 * Matches the single retry the settings provider has always used for this key,
	 * with the rate-limit short circuit the per-feature reads relied on. Retrying
	 * harder would delay surfacing errors on a query that now backs three
	 * surfaces at once.
	 */
	retry: (failureCount: number, error: GatewayError): boolean => {
		if (error?.statusCode === 429) {
			return false;
		}
		return failureCount < 1;
	},
});

/**
 * Observes the shared admin-settings query and projects a per-feature view.
 *
 * Because every caller shares the same query key, mounting any number of
 * selectors costs a single fetch; `select` runs per observer without widening
 * the cache.
 */
export function useAdminSettingsSelector<TData>({
	siteId,
	select,
	enabled = true,
	onError,
}: {
	siteId: string | undefined;
	select: (settings: RovoExtAdminSettings) => TData;
	enabled?: boolean;
	onError?: (error: GatewayError) => void;
}): UseQueryResult<TData, GatewayError> {
	const options = getAdminSettingsQueryOptions(siteId);

	return useQuery<RovoExtAdminSettings, GatewayError, TData>({
		...options,
		enabled: options.enabled !== false && enabled,
		select,
		throwOnError(error) {
			onError?.(error);
			return false;
		},
	});
}
